import { useNavigate } from 'react-router-dom'
import {
  Users,
  Package,
  FileText,
  UserCog,
  Lock,
  Tags,
  FileEdit,
  Store,
  CalendarDays,
  ChevronsUpDown,
  ChevronRight,
  type LucideIcon,
  Warehouse,
  Settings,
  BarChart3,
  Workflow,
  Truck,
  Table,
  Trash2,
} from 'lucide-react'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'

interface AdminItem {
  label: string
  icon: LucideIcon
  href: string
}

interface AdminSection {
  title: string
  description: string
  icon: LucideIcon
  items: AdminItem[]
}

const adminSections: AdminSection[] = [
  {
    title: 'Users and Roles',
    description: 'Manage user accounts, roles, and access permissions',
    icon: Users,
    items: [
      { label: 'Manage Users', icon: UserCog, href: '/admin/users' },
      {
        label: 'Manage Assortment Access',
        icon: Lock,
        href: '/admin/assortment-access',
      },
      {
        label: 'Manage Report Permissions',
        icon: BarChart3,
        href: '/admin/report-permissions',
      },
      {
        label: 'Deleted Plans',
        icon: Trash2,
        href: '/plans/deleted',
      },
    ],
  },
  {
    title: 'Configuration',
    description: 'Manage dropdowns, lookups, and plan defaults',
    icon: Settings,
    items: [
      {
        label: 'Dropdown Values',
        icon: ChevronsUpDown,
        href: '/admin/dropdowns',
      },
      {
        label: 'Lookups',
        icon: Warehouse,
        href: '/admin/warehouse-lookups',
      },
      {
        label: 'System Defaults',
        icon: Settings,
        href: '/admin/system-settings',
      },
    ],
  },
  {
    title: 'SKUs',
    description: 'Configure SKU attributes and settings',
    icon: Package,
    items: [
      {
        label: 'Manage SKU Attributes',
        icon: Tags,
        href: '/admin/sku-attributes',
      },
    ],
  },
  {
    title: 'Manage Documents',
    description: 'Edit document templates and content',
    icon: FileText,
    items: [
      {
        label: 'Commitment Letter Template',
        icon: FileEdit,
        href: '/admin/document-templates',
      },
    ],
  },
  {
    title: 'Stores',
    description: 'Browse and manage store data',
    icon: Store,
    items: [
      {
        label: 'Browse Stores',
        icon: Store,
        href: '/admin/stores',
      },
    ],
  },
  {
    title: 'Calendars',
    description: 'Create and manage workflow calendars with milestone tracking',
    icon: CalendarDays,
    items: [
      {
        label: 'Calendars',
        icon: CalendarDays,
        href: '/admin/clt-calendars',
      },
      {
        label: 'Calendar Logic Templates',
        icon: Workflow,
        href: '/admin/calendar-logic-templates',
      },
      {
        label: 'Backup Days Matrix',
        icon: Truck,
        href: '/admin/backup-days-matrix',
      },
      {
        label: 'Calendar Lookup Table',
        icon: Table,
        href: '/admin/calendar-lookup',
      },
    ],
  },
]

export default function Admin() {
  const navigate = useNavigate()

  return (
    <div className="flex flex-col gap-6 p-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-semibold text-foreground">
          Admin Settings
        </h1>
        <p className="mt-1 text-base text-muted-foreground">
          Manage users, roles, SKUs, document templates, and utilities
        </p>
      </div>

      {/* Section Cards Grid */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {adminSections.map(section => {
          const SectionIcon = section.icon
          return (
            <Card key={section.title}>
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                    <SectionIcon className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <CardTitle className="text-lg">{section.title}</CardTitle>
                    <CardDescription className="mt-1">
                      {section.description}
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {section.items.map(item => {
                    const ItemIcon = item.icon
                    return (
                      <li key={item.href}>
                        <button
                          onClick={() => navigate(item.href)}
                          className="flex w-full cursor-pointer items-center justify-between rounded-md p-2 text-left text-sm transition-colors hover:bg-muted"
                        >
                          <div className="flex items-center gap-3">
                            <ItemIcon className="h-4 w-4 text-muted-foreground" />
                            <span>{item.label}</span>
                          </div>
                          <ChevronRight className="h-4 w-4 text-muted-foreground" />
                        </button>
                      </li>
                    )
                  })}
                </ul>
              </CardContent>
            </Card>
          )
        })}
      </div>
    </div>
  )
}
